import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserPlus, KeyRound } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ts, P } from '../api';
import Avatar from '../Avatar';
import ProfileEditor from '../ProfileEditor';
import {
  Page, Section, Field, Select, Table, Td, Pill, VoicePill, SmsPill, Loading, ErrorNote, Empty,
  ROLE_LABELS, useLoad, useTS, when,
} from '../ui';

const ASSIGNABLE = { owner: ['administrator', 'supervisor', 'sales_employee'], administrator: ['supervisor', 'sales_employee'] };

export default function TSStaff() {
  const session = useTS();
  const staff = useLoad(() => ts.staff());
  const numbers = useLoad(() => ts.numbers());
  const [adding, setAdding] = useState(false);
  const [secret, setSecret] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [managing, setManaging] = useState(null);
  const [code, setCode] = useState(null);
  const canManage = session.can(P.STAFF);
  const canAssign = session.can(P.NUMBERS);
  const roles = ASSIGNABLE[session.role] || [];

  const reload = () => { staff.reload(); numbers.reload(); };
  const act = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(ok); reload(); return r; } catch (e) { toast.error(e.message); return null; }
  };
  const issueCode = async (m) => {
    const r = await act(() => ts.issueCode(m.id));
    if (r) setCode({ ...r, name: m.full_name });
  };

  if (staff.loading && !staff.data) return <Loading />;
  const nums = numbers.data || [];
  const free = nums.filter(n => n.role !== 'main' && !n.assigned_member_id && !n.seat_released && n.status !== 'unavailable');

  return (
    <Page title="Staff & Numbers" subtitle={`${nums.length} company number${nums.length === 1 ? '' : 's'} · ${free.length} unassigned`}
      actions={canManage && <Button size="sm" onClick={() => setAdding(true)} data-testid="ts-add-staff"><UserPlus className="w-4 h-4 mr-1" /> Add employee</Button>}>
      <ErrorNote error={staff.error || numbers.error} />
      {secret && (
        <Section>
          <div className="text-sm" data-testid="ts-temp-password">
            Temporary password for <strong>{secret.name}</strong>: <code className="px-1.5 py-0.5 bg-muted rounded">{secret.password}</code>
            <p className="text-xs text-muted-foreground mt-1">Share it privately. It's shown only once and must be changed at first sign-in.</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setSecret(null)}>Done</Button>
        </Section>
      )}
      {code && <CodePanel code={code} onClose={() => setCode(null)} />}
      {adding && <AddEmployee roles={roles} free={free} onClose={() => setAdding(false)}
        onAdded={(m) => {
          setAdding(false);
          if (m.temporary_password) setSecret({ name: m.full_name, password: m.temporary_password });
          reload();
          const num = free.find(n => n.id === m.assigned_number_id);
          if (num && num.status === 'voice_ready') issueCode(m);
          else toast.success(`${m.full_name} added. Assign a number, then generate their activation code.`);
        }} />}
      {managing && (staff.data || []).some(m => m.id === managing) && (
        <Manage key={managing} member={staff.data.find(m => m.id === managing)} session={session}
          staff={staff.data} canAssign={canAssign} act={act} onIssue={issueCode} onClose={() => setManaging(null)} />
      )}

      {editing && (staff.data || []).some(m => m.id === editing) && (
        <Section title={`${staff.data.find(m => m.id === editing).full_name} — username & photo`}
          actions={<Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Close</Button>}>
          <ProfileEditor key={editing} member={staff.data.find(m => m.id === editing)}
            onSaved={async () => { staff.reload(); if (editing === session.member.id) await session.refresh(); }} />
        </Section>
      )}
      <Section title="Team">
        {(staff.data || []).length === 0 ? <Empty>No staff yet.</Empty> : (
          <Table head={['Name', 'Role', 'Number', 'Status', 'Aster Phone', '']}>
            {staff.data.map(m => (
              <tr key={m.id} data-testid={`ts-staff-row-${m.id}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <Avatar id={m.id} name={m.full_name} version={m.avatar_version} />
                    <div>
                      <div className="font-medium text-foreground">{m.full_name}</div>
                      {m.handle && <div className="text-xs text-foreground">@{m.handle}</div>}
                      <div className="text-xs text-muted-foreground">
                        {m.extension ? `Ext. ${m.extension}` : ''}{m.extension && m.department ? ' · ' : ''}{m.department}
                      </div>
                      {m.email && <div className="text-xs text-muted-foreground">{m.email}</div>}
                    </div>
                  </div>
                </Td>
                <Td>
                  {canManage && m.role !== 'owner' && m.id !== session.member.id && roles.includes(m.role)
                    ? <Select value={m.role} onChange={(r) => act(() => ts.changeRole(m.id, r), 'Role updated')}
                      options={roles.map(r => [r, ROLE_LABELS[r]])} />
                    : ROLE_LABELS[m.role]}
                </Td>
                <Td>
                  {m.assigned_number ? (
                    <div className="space-y-1">
                      <div className="font-medium">{m.assigned_number.display}</div>
                      <div className="flex flex-wrap gap-1"><VoicePill status={m.assigned_number.voice_status} /><SmsPill status={m.assigned_number.sms_status} /></div>
                      {canAssign && <button className="text-xs text-muted-foreground hover:text-red-500" onClick={() => act(() => ts.unassignNumber(m.id), 'Number unassigned')}>Unassign</button>}
                    </div>
                  ) : canAssign && m.status === 'active' ? (
                    <Select value="" onChange={(nid) => nid && act(() => ts.assignNumber(m.id, nid), 'Number assigned')}
                      options={[['', free.length ? 'Assign a number…' : 'No free numbers'], ...free.map(n => [n.id, n.display])]} data-testid={`ts-assign-${m.id}`} />
                  ) : <span className="text-muted-foreground text-xs">None</span>}
                </Td>
                <Td>
                  <Pill tone={m.status === 'active' ? 'green' : 'red'}>{m.status}</Pill>
                  <div className="text-xs text-muted-foreground mt-1">{m.availability || 'offline'}</div>
                </Td>
                <Td><ActivationPill member={m} /></Td>
                <Td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setViewing(m.id)}>Activity</Button>
                  {(m.id === session.member.id || (canManage && canEditProfile(session, m))) && (
                    <Button size="sm" variant="ghost" onClick={() => setEditing(m.id)} data-testid={`ts-edit-profile-${m.id}`}>Username & photo</Button>
                  )}
                  {canManage && m.role !== 'owner' && m.id !== session.member.id && (
                    <>
                      {m.status === 'active' && (
                        <Button size="sm" variant="ghost" onClick={() => issueCode(m)} data-testid={`ts-code-${m.id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" />{m.activation_status === 'activated' ? 'New code' : 'Activation code'}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setManaging(m.id)} data-testid={`ts-manage-${m.id}`}>Manage</Button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {canAssign && (
        <Section title="Company numbers">
          {nums.length === 0 ? <Empty>No numbers yet.</Empty> : (
            <Table head={['Number', 'Voice', 'SMS', 'Assigned to']}>
              {nums.map(n => (
                <tr key={n.id}>
                  <Td className="font-medium">{n.display}{n.seat_released && <Pill tone="gray">Seat removed</Pill>}</Td>
                  <Td><VoicePill status={n.status} /></Td>
                  <Td><SmsPill status={n.sms_status} /></Td>
                  <Td>{n.assigned_member_name || <span className="text-muted-foreground">Unassigned</span>}</Td>
                </tr>
              ))}
            </Table>
          )}
          <p className="text-xs text-muted-foreground">Numbers belong to the company. When someone leaves, their number stays here for the next person and their call history stays with the company.</p>
        </Section>
      )}
      {viewing && <Activity memberId={viewing} onClose={() => setViewing(null)} />}
    </Page>
  );
}

// Mirrors the server: nobody but the owner edits the owner; administrators
// don't edit other administrators.
function canEditProfile(session, m) {
  if (m.role === 'owner') return session.role === 'owner';
  if (session.role === 'administrator' && m.role === 'administrator') return m.id === session.member.id;
  return true;
}

function AddEmployee({ roles, free, onClose, onAdded }) {
  const [form, setForm] = useState({ full_name: '', email: '', role: 'sales_employee', department: '', extension: '', forward_phone: '', number_id: free[0]?.id || '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...form, number_id: form.number_id || null, email: form.email.trim(),
      extension: form.extension ? Number(form.extension) : null, forward_phone: form.forward_phone || '' };
    try { onAdded(await ts.addStaff(body)); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  return (
    <Section title="Add employee">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ts-add-staff-form">
        <ErrorNote error={error} />
        <Field label="Full name"><Input required value={form.full_name} onChange={set('full_name')} data-testid="ts-staff-name" /></Field>
        <Field label="Work email (optional)" hint="Used only for account recovery. They sign in to Aster Phone with their number and a one-time code.">
          <Input type="email" value={form.email} onChange={set('email')} data-testid="ts-staff-email" />
        </Field>
        <Field label="Team / department (optional)"><Input value={form.department} onChange={set('department')} placeholder="Sales" data-testid="ts-staff-dept" /></Field>
        <Field label="Extension" hint="Leave blank to assign the next free one (from 101).">
          <Input inputMode="numeric" value={form.extension} onChange={set('extension')} placeholder="Auto" data-testid="ts-staff-ext" />
        </Field>
        <Field label="Role"><Select value={form.role} onChange={set('role')} options={roles.map(r => [r, ROLE_LABELS[r]])} /></Field>
        <Field label="Company number">
          <Select value={form.number_id} onChange={set('number_id')} options={[['', 'Assign later'], ...free.map(n => [n.id, n.display])]} data-testid="ts-staff-number" />
        </Field>
        <Field label="Backup phone (optional)" hint="Only used if you switch them to ring their own phone instead of the app.">
          <Input value={form.forward_phone} onChange={set('forward_phone')} placeholder="+1 555 123 4567" data-testid="ts-staff-forward" />
        </Field>
        <div className="sm:col-span-2 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy} data-testid="ts-staff-submit">Add employee</Button>
        </div>
      </form>
    </Section>
  );
}

function Activity({ memberId, onClose }) {
  const { data, loading, error } = useLoad(() => ts.staffActivity(memberId), [memberId]);
  return (
    <Section title={data ? `${data.member.full_name} — activity` : 'Activity'} actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      {loading ? <Loading /> : error ? <ErrorNote error={error} /> : (
        <div className="space-y-3 text-sm">
          <div className="flex gap-4"><span>Talk minutes (recent): <strong>{data.minutes}</strong></span><span>Texts sent: <strong>{data.messages_sent}</strong></span></div>
          <Table head={['When', 'Direction', 'Customer', 'Status', 'Duration']}>
            {data.recent_calls.map(c => (
              <tr key={c.id}><Td>{when(c.created_at)}</Td><Td>{c.direction}</Td><Td>{c.customer_number}</Td><Td>{c.status}</Td><Td>{c.duration_seconds || 0}s</Td></tr>
            ))}
          </Table>
          <div>
            <div className="text-xs font-medium mb-1">Number history</div>
            {data.assignment_history.length === 0 ? <p className="text-xs text-muted-foreground">No assignments.</p> : (
              <ul className="text-xs text-muted-foreground space-y-0.5">
                {data.assignment_history.map((h, i) => <li key={i}>{h.e164 || h.number_id}: {when(h.assigned_at)} → {h.unassigned_at ? when(h.unassigned_at) : 'now'}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

const ACTIVATION = {
  activated: ['green', 'Activated'],
  code_issued: ['amber', 'Code issued'],
  not_activated: ['gray', 'Not activated'],
};

function ActivationPill({ member }) {
  const [tone, label] = ACTIVATION[member.activation_status] || ACTIVATION.not_activated;
  return <Pill tone={tone} data-testid={`ts-activation-${member.id}`}>{label}</Pill>;
}

function useCountdown(iso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));
  return left;
}

// The code is shown once. The server keeps only a keyed hash of it.
function CodePanel({ code, onClose }) {
  const left = useCountdown(code.expires_at);
  const mm = String(Math.floor(left / 60)).padStart(1, '0');
  const ss = String(left % 60).padStart(2, '0');
  return (
    <Section title={`Activation code for ${code.name}`} actions={<Button size="sm" variant="ghost" onClick={onClose}>Done</Button>}>
      <div className="flex flex-wrap items-center gap-6" data-testid="ts-activation-code">
        <div>
          <div className="text-xs text-muted-foreground">Code</div>
          <div className="text-3xl font-mono font-semibold tracking-[0.3em] text-foreground" data-testid="ts-activation-code-value">{code.code}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Their number</div>
          <div className="font-medium">{code.display || code.number}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Expires in</div>
          <div className={`font-medium ${left === 0 ? 'text-red-500' : ''}`}>{left === 0 ? 'Expired' : `${mm}:${ss}`}</div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Give this code to the employee in person or by a private message. They open Aster Phone (the app, or <a href="/phone/" target="_blank" rel="noreferrer" className="underline" data-testid="ts-phone-app-link">agent9.me/phone</a> on any phone or computer, where they can install it), enter their number and this code.
        It works once, for {code.minutes} minutes, and locks after 5 wrong tries. It's shown only now. Generating a new code cancels this one.
      </p>
    </Section>
  );
}

function Manage({ member: m, session, staff, canAssign, act, onIssue, onClose }) {
  const isOwner = session.role === 'owner';
  const [form, setForm] = useState({
    extension: m.extension || '', department: m.department || '', email: m.email || '',
    call_mode: m.call_mode || 'app', forward_phone: m.forward_phone || '',
  });
  const devices = useLoad(() => ts.memberDevices(m.id), [m.id]);
  const [transferTo, setTransferTo] = useState('');
  const set = (k) => (v) => setForm({ ...form, [k]: typeof v === 'string' ? v : v.target.value });
  const save = () => {
    const body = { ...form, email: form.email.trim() };
    if (form.extension === '') delete body.extension; else body.extension = Number(form.extension);
    if (form.call_mode !== 'phone' && !form.forward_phone) delete body.forward_phone;
    return act(() => ts.updateMember(m.id, body), 'Saved');
  };
  const toggle = (key) => act(() => ts.updateMember(m.id, { [key]: !m[key] }), 'Access updated');
  const others = staff.filter(o => o.id !== m.id && o.status === 'active' && !o.assigned_number && o.role !== 'owner');

  return (
    <Section title={`Manage ${m.full_name}`} actions={<Button size="sm" variant="ghost" onClick={onClose}>Close</Button>}>
      <div data-testid="ts-manage-panel" className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Extension"><Input inputMode="numeric" value={form.extension} onChange={set('extension')} data-testid="ts-manage-ext" /></Field>
          <Field label="Team / department"><Input value={form.department} onChange={set('department')} data-testid="ts-manage-dept" /></Field>
          <Field label="Recovery email"><Input type="email" value={form.email} onChange={set('email')} /></Field>
          <Field label="Calls ring" hint="The app is the default. Their own phone is a fallback you can turn on.">
            <Select value={form.call_mode} onChange={set('call_mode')} data-testid="ts-manage-callmode"
              options={[['app', 'In the Aster Phone app'], ['phone', 'Their own phone (fallback)']]} />
          </Field>
          {form.call_mode === 'phone' && (
            <Field label="Their phone"><Input value={form.forward_phone} onChange={set('forward_phone')} placeholder="+1 555 123 4567" /></Field>
          )}
        </div>
        <div className="flex justify-end"><Button size="sm" onClick={save} data-testid="ts-manage-save">Save</Button></div>

        <div className="space-y-2">
          <div className="text-xs font-medium">Sign-in & devices</div>
          <div className="flex flex-wrap gap-2">
            {m.status === 'active' && <Button size="sm" variant="outline" onClick={() => onIssue(m)}>Generate activation code</Button>}
            {m.activation_status === 'code_issued' && (
              <Button size="sm" variant="outline" onClick={() => act(() => ts.revokeCodes(m.id), 'Code cancelled')} data-testid="ts-manage-revoke-code">Cancel code</Button>
            )}
            <Button size="sm" variant="outline" onClick={() => act(() => ts.forceLogout(m.id), 'Signed out everywhere').then(devices.reload)}
              data-testid="ts-manage-force-logout">Sign out everywhere</Button>
            {m.email && (
              <Button size="sm" variant="outline" onClick={async () => {
                const r = await act(() => ts.staffAction(m.id, 'reset-credentials'));
                if (r) toast.success(`Temporary password for ${m.full_name}: ${r.temporary_password}`, { duration: 30000 });
              }}>Reset password</Button>
            )}
          </div>
          {devices.loading ? <Loading /> : (devices.data || []).length === 0 ? (
            <p className="text-xs text-muted-foreground">No signed-in devices.</p>
          ) : (
            <Table head={['Device', 'App', 'Last seen', '']}>
              {devices.data.map(d => (
                <tr key={d.id} data-testid={`ts-device-${d.id}`}>
                  <Td>{d.model || d.platform}<div className="text-xs text-muted-foreground">{d.platform}</div></Td>
                  <Td>{d.app_version || '—'}</Td>
                  <Td>{when(d.last_seen_at)}</Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" className="text-red-500"
                      onClick={() => act(() => ts.revokeDevice(m.id, d.id), 'Device signed out').then(devices.reload)}>Sign out device</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </div>

        {canAssign && m.assigned_number && (
          <div className="space-y-2">
            <div className="text-xs font-medium">Transfer their number ({m.assigned_number.display})</div>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={transferTo} onChange={setTransferTo} data-testid="ts-manage-transfer"
                options={[['', others.length ? 'Choose a teammate without a number…' : 'No teammate without a number'], ...others.map(o => [o.id, o.full_name])]} />
              <Button size="sm" variant="outline" disabled={!transferTo}
                onClick={() => act(() => ts.transferNumber(m.id, transferTo), 'Number transferred')}>Transfer</Button>
            </div>
            <p className="text-xs text-muted-foreground">Their sign-in on that number ends. The new person needs an activation code.</p>
          </div>
        )}

        {isOwner && (
          <div className="space-y-2">
            <div className="text-xs font-medium">Owner-only access</div>
            {m.role === 'administrator' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!m.comms_access} onChange={() => toggle('comms_access')} data-testid="ts-manage-comms" />
                Can review the Communications Center (chats, texts, calls)
              </label>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!m.portal_access} onChange={() => toggle('portal_access')} data-testid="ts-manage-portal" />
              Can also sign in to this web portal (otherwise Aster Phone only)
            </label>
          </div>
        )}

        <div className="flex flex-wrap gap-2 border-t border-border pt-3">
          {m.status === 'active'
            ? <Button size="sm" variant="outline" className="text-red-500" data-testid="ts-manage-suspend"
              onClick={() => act(() => ts.setStatus(m.id, 'suspend'), 'Suspended. Their calls and sessions ended.')}>Suspend</Button>
            : <Button size="sm" variant="outline" onClick={() => act(() => ts.setStatus(m.id, 'reactivate'), 'Reactivated')}>Reactivate</Button>}
          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => {
            if (window.confirm(`Remove ${m.full_name}? Their number stays with the company and their history is kept.`)) {
              act(() => ts.setStatus(m.id, 'remove'), 'Employee removed').then(onClose);
            }
          }}>Remove</Button>
        </div>
      </div>
    </Section>
  );
}
